/*
 * gr-size-calc.js — Motor de recomendación de tallas (GomezRosario)
 * =================================================================
 * FUNCIÓN PURA, sin dependencias, sin llamadas externas, sin estado global.
 * La UI (cuestionario del drawer) se construye aparte y solo consume `recommendSize`.
 *
 * LÓGICA A NIVEL HUMANO
 * ---------------------
 * La talla se plantea como: "¿qué talla te entra con la holgura que te gusta llevar?".
 *
 *  1. Tabla del proveedor -> contorno real. Las medidas del proveedor son PLANAS (media,
 *     "de lado a lado" sobre la prenda), así que el contorno de la prenda = medida plana × 2.
 *  2. Contornos del cliente (pecho/cintura/cadera):
 *       - Los que da (cintura, cadera, y pecho vía talla de sujetador) se usan tal cual.
 *       - Los que faltan se ESTIMAN desde altura + peso con un modelo antropométrico
 *         (circunferencia ∝ √(peso/altura); ver estimateBodyCirc).
 *  3. La preferencia de fit fija una HOLGURA OBJETIVO (muy pegada -> poca; muy holgada -> mucha).
 *  4. Para cada talla se mira la "holgura de la zona más ajustada" (la menor holgura entre
 *     pecho/cintura/cadera): esa es la que decide el ajuste, porque una prenda de tejido no
 *     elástico no puede apretar en ningún sitio. Se elige la talla cuya holgura en esa zona
 *     más ajustada queda más cerca de la holgura objetivo, sin quedar por debajo del mínimo
 *     para poder ponérsela. Esto adapta la recomendación tanto a la FORMA del cuerpo como al
 *     CORTE de la prenda automáticamente (una prenda holgada de cintura no fuerza tallas
 *     pequeñas: solo manda la zona donde el cuerpo va justo).
 *
 * Devuelve además un nivel de CONFIANZA (baja si tuvo que estimar la zona que decide, o si dos
 * tallas quedan empatadas, o si el cuerpo se sale de la tabla).
 *
 * Todo el motor trabaja en CM y KG. La UI convierte in->cm y lb->kg antes de llamar (o usa los
 * helpers inToCm / lbToKg exportados).
 *
 * CALIBRACIÓN (las dos únicas piezas "de research", ajustables sin tocar la mecánica):
 *   - PROFILES: estimador altura/peso -> contornos (derivado de proporciones antropométricas EU).
 *   - FIT_EASE / MIN_EASE: holguras por nivel de fit y mínimos para tejido normal vs elástico.
 * Para recalibrar, cambiar solo CONFIG. Ejecutar el banco de pruebas:  node assets/gr-size-calc.js --selftest
 */

(function (root) {
  'use strict';

  // ==========================================================================================
  //  CONFIG — todo lo calibrable en un sitio
  // ==========================================================================================
  var CONFIG = {
    /*
     * Estimador de contornos corporales: C = k * sqrt(W/H) + b   (W en kg, H en cm) -> cm.
     * Modelo físico: circunferencia ∝ √(área transversal); área ∝ volumen/altura; volumen ∝ peso
     * (densidad ~ constante) => C ∝ √(W/H). Coeficientes calibrados contra tablas de medidas
     * corporales femeninas EU estándar (EU34≈bust80/waist62/hip88 ... EU44≈bust100/waist82/hip108),
     * verificados como casi lineales en √(W/H). El set 'male' es aproximado (sin bra), marcado.
     */
    PROFILES: {
      female: {
        bust:  { k: 124.2, b: 12.3 },
        waist: { k: 161.5, b: -27.2 },
        hip:   { k: 142.9, b: 7.5 }
      },
      male: { // aproximado — recalibrar si se usa en producto de hombre
        approx: true,
        bust:  { k: 122.9, b: 17.1 },
        waist: { k: 157.1, b: -15.75 },
        hip:   { k: 95.6,  b: 34.7 }
      }
    },

    /*
     * Copa de sujetador (convención EU): diferencia bust - underbust en cm ≈ 11 + 2*index,
     * con AA=0, A=1, B=2 ... K=11 (cada copa ≈ +2 cm). bust = underbust + diferencia.
     * Ej.: 75B -> 75 + (11 + 2*2) = 90 cm.
     */
    CUP_INDEX: { 'AA': 0, 'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8, 'I': 9, 'J': 10, 'K': 11 },
    CUP_BASE: 11,
    CUP_STEP: 2,

    /*
     * Holgura OBJETIVO (contorno completo, cm) en la zona más ajustada, por nivel de fit:
     * 0 Muy pegada · 1 Pegada · 2 Normal · 3 Holgada · 4 Muy holgada.
     */
    FIT_EASE: [2, 5, 9, 15, 22],

    MIN_EASE_WOVEN: 2,    // holgura mínima para poder ponérsela (tejido no elástico)
    MIN_EASE_STRETCH: -4, // el tejido elástico admite holgura negativa
    STRETCH_SHIFT: -4,    // el objetivo baja con tejido elástico

    LOOSE_MARGIN: 6,      // si la talla más pequeña supera el objetivo por más de esto -> avisar "quedará holgada"
    ALT_TOL: 2            // si la 2ª mejor talla queda a <= esto de distancia -> se ofrece como alternativa
  };

  // ==========================================================================================
  //  Helpers
  // ==========================================================================================
  function num(v) { return (typeof v === 'number' && isFinite(v)) ? v : null; }
  function inToCm(inches) { var n = num(inches); return n == null ? null : n * 2.54; }
  function lbToKg(lb) { var n = num(lb); return n == null ? null : n * 0.45359237; }

  /* Contorno de pecho a partir de la talla de sujetador (bajo pecho + copa). */
  function bustFromBra(underbustCm, cupLabel) {
    var u = num(underbustCm);
    if (u == null || !cupLabel) return null;
    var idx = CONFIG.CUP_INDEX[String(cupLabel).toUpperCase()];
    if (idx == null) return null; // '—' u otra -> sin dato
    return u + (CONFIG.CUP_BASE + CONFIG.CUP_STEP * idx);
  }

  /* Estima {bust, waist, hip} desde altura(cm) + peso(kg). Devuelve null si faltan datos. */
  function estimateBodyCirc(heightCm, weightKg, profile) {
    var H = num(heightCm), W = num(weightKg);
    if (H == null || W == null || H <= 0 || W <= 0) return null;
    var p = CONFIG.PROFILES[profile] || CONFIG.PROFILES.female;
    var x = Math.sqrt(W / H);
    return {
      bust:  p.bust.k * x + p.bust.b,
      waist: p.waist.k * x + p.waist.b,
      hip:   p.hip.k * x + p.hip.b
    };
  }

  // ==========================================================================================
  //  Núcleo: recommendSize(inputs, chart)
  // ==========================================================================================
  /*
   * inputs = {
   *   heightCm, weightKg,            // obligatorios (para estimar lo que falte)
   *   fit,                           // 0..4  (muy pegada .. muy holgada). Default 2 (normal)
   *   waistCm?, hipCm?,              // opcionales (contornos reales del cliente)
   *   bust?: { underbust, cup },     // opcional (talla de sujetador -> contorno de pecho)
   *   profile?                       // 'female' (default) | 'male'
   * }
   * chart = {
   *   flat: true, stretch: false, unit: 'cm',
   *   sizes: { S:{chest,waist,hip,length}, M:{...}, ... }  // medidas del proveedor
   * }                                   // las claves de talla se listan de menor a mayor
   *
   * -> { size, alt, confidence, score, fit, bindingMeasure, desiredEase,
   *      tooTight, tooLoose, maxedOut, bodyCirc, source, perMeasure }
   */
  function recommendSize(inputs, chart) {
    inputs = inputs || {};
    if (!chart || !chart.sizes) return { size: null, reason: 'no-chart' };
    var sizeKeys = Object.keys(chart.sizes);
    if (sizeKeys.length === 0) return { size: null, reason: 'no-chart' };
    if (sizeKeys.length === 1) return { size: sizeKeys[0], confidence: 'media', score: 0.7, reason: 'single-size' };

    var flat = chart.flat !== false;      // por defecto true (medidas planas)
    var stretch = !!chart.stretch;
    var fit = Math.max(0, Math.min(4, inputs.fit == null ? 2 : (inputs.fit | 0)));

    // Medidas que define la tabla (según la primera talla de referencia)
    var ref = chart.sizes[sizeKeys[0]];
    var measures = [];
    if (num(ref.chest) != null) measures.push({ key: 'chest', body: 'bust' });
    if (num(ref.waist) != null) measures.push({ key: 'waist', body: 'waist' });
    if (num(ref.hip) != null) measures.push({ key: 'hip', body: 'hip' });
    if (measures.length === 0) return { size: null, reason: 'no-measures' };

    // Contornos del cliente (dados o estimados)
    var est = estimateBodyCirc(num(inputs.heightCm), num(inputs.weightKg), inputs.profile || 'female');
    var bustReal = inputs.bust ? bustFromBra(inputs.bust.underbust, inputs.bust.cup) : null;
    var body = {}, source = {};
    body.bust = bustReal != null ? bustReal : (est ? est.bust : null);
    source.bust = bustReal != null ? 'provided' : 'estimated';
    body.waist = num(inputs.waistCm) != null ? num(inputs.waistCm) : (est ? est.waist : null);
    source.waist = num(inputs.waistCm) != null ? 'provided' : 'estimated';
    body.hip = num(inputs.hipCm) != null ? num(inputs.hipCm) : (est ? est.hip : null);
    source.hip = num(inputs.hipCm) != null ? 'provided' : 'estimated';

    // Holgura objetivo y mínimo según fit / tejido
    var minEase = stretch ? CONFIG.MIN_EASE_STRETCH : CONFIG.MIN_EASE_WOVEN;
    var desired = CONFIG.FIT_EASE[fit] + (stretch ? CONFIG.STRETCH_SHIFT : 0);
    if (desired < minEase) desired = minEase;

    // Holgura de la zona más ajustada por talla
    var rows = sizeKeys.map(function (sk) {
      var g = chart.sizes[sk];
      var perM = {}, bind = Infinity, bindKey = null;
      measures.forEach(function (m) {
        var b = body[m.body];
        if (b == null) return;
        var garmentCirc = flat ? num(g[m.key]) * 2 : num(g[m.key]);
        if (garmentCirc == null) return;
        var ease = garmentCirc - b;
        perM[m.key] = { garment: garmentCirc, body: round1(b), ease: round1(ease), source: source[m.body] };
        if (ease < bind) { bind = ease; bindKey = m.key; }
      });
      return { size: sk, bindingEase: bind, bindingKey: bindKey, perM: perM };
    });

    var lastKey = sizeKeys[sizeKeys.length - 1];
    var fitting = rows.filter(function (r) { return r.bindingEase >= minEase; });
    var result, tooTight = false, tooLoose = false, maxedOut = false, alt = null;

    if (fitting.length === 0) {
      // Ni la talla más grande entra: se recomienda la más grande y se avisa
      result = rows.reduce(function (a, b) { return b.bindingEase > a.bindingEase ? b : a; });
      tooTight = true;
    } else {
      // Talla cuya holgura en la zona ajustada queda más cerca del objetivo; empate -> más pequeña
      fitting.sort(function (a, b) {
        var da = Math.abs(a.bindingEase - desired), db = Math.abs(b.bindingEase - desired);
        if (da !== db) return da - db;
        return sizeKeys.indexOf(a.size) - sizeKeys.indexOf(b.size);
      });
      result = fitting[0];
      if (fitting[1]) {
        var d0 = Math.abs(result.bindingEase - desired);
        var d1 = Math.abs(fitting[1].bindingEase - desired);
        if (Math.abs(d1 - d0) <= CONFIG.ALT_TOL) alt = fitting[1].size;
      }
      // No se puede tan ceñida como pide: incluso la más pequeña supera el objetivo
      if (result.size === sizeKeys[0] && (result.bindingEase - desired) > CONFIG.LOOSE_MARGIN) tooLoose = true;
      // No se puede tan holgada como pide: la más grande sigue por debajo del objetivo
      if (result.size === lastKey && result.bindingEase < desired - 1) maxedOut = true;
    }

    // Confianza
    var score = 1.0;
    if (result.bindingKey && result.perM[result.bindingKey].source === 'estimated') score *= 0.8;
    if (alt) score *= 0.85;
    if (tooLoose) score *= 0.85;
    if (maxedOut) score *= 0.95;
    if (tooTight) score = Math.min(score, 0.45);
    var allEst = measures.every(function (m) { return source[m.body] === 'estimated'; });
    if (allEst && !tooTight) score *= 0.9;
    var confidence = score >= 0.8 ? 'alta' : (score >= 0.6 ? 'media' : 'orientativa');

    return {
      size: result.size,
      alt: alt,
      confidence: confidence,
      score: Math.round(score * 100) / 100,
      fit: fit,
      bindingMeasure: result.bindingKey,
      desiredEase: desired,
      tooTight: tooTight,
      tooLoose: tooLoose,
      maxedOut: maxedOut,
      bodyCirc: { bust: round1(body.bust), waist: round1(body.waist), hip: round1(body.hip) },
      source: source,
      perMeasure: result.perM
    };
  }

  function round1(v) { return v == null ? null : Math.round(v * 10) / 10; }

  // ==========================================================================================
  //  Banco de pruebas (research + validación). No se ejecuta en producción.
  // ==========================================================================================
  var DRESS = { flat: true, stretch: false, sizes: {
    S: { chest: 44, waist: 51, hip: 61, length: 77 },
    M: { chest: 46, waist: 53, hip: 64, length: 78 },
    L: { chest: 48, waist: 55, hip: 67, length: 79 }
  } };
  var PANTS = { flat: true, stretch: false, sizes: {
    S: { waist: 38, hip: 52 }, M: { waist: 40, hip: 54 }, L: { waist: 42, hip: 56 }
  } };

  function selfTest() {
    var cases = [
      { name: 'Estimador: H165/W55 (EU36 esperado)', kind: 'est', h: 165, w: 55, expect: { bust: 84, waist: 66, hip: 90 }, tol: 2 },
      { name: 'Estimador: H175/W85 (curvy alta)', kind: 'est', h: 175, w: 85, expect: { bust: 99, waist: 85, hip: 107 }, tol: 3 },
      { name: 'Bra 75B -> pecho 90', kind: 'bra', ub: 75, cup: 'B', expect: 90, tol: 0.5 },
      { name: 'Vestido · solo H/W · normal -> M', in: { heightCm: 165, weightKg: 55, fit: 2 }, chart: DRESS, expect: 'M' },
      { name: 'Vestido · solo H/W · muy pegada -> S', in: { heightCm: 165, weightKg: 55, fit: 0 }, chart: DRESS, expect: 'S' },
      { name: 'Vestido · solo H/W · muy holgada -> L', in: { heightCm: 165, weightKg: 55, fit: 4 }, chart: DRESS, expect: 'L' },
      { name: 'Vestido · pecho grande (bra 80D) -> L + tooTight', in: { heightCm: 168, weightKg: 68, fit: 2, waistCm: 74, hipCm: 100, bust: { underbust: 80, cup: 'D' } }, chart: DRESS, expect: 'L', tooTight: true },
      { name: 'Vestido · pear (cadera 108) · normal -> L', in: { heightCm: 165, weightKg: 68, fit: 2, waistCm: 70, hipCm: 108 }, chart: DRESS, expect: 'L' },
      { name: 'Pantalón (sin pecho) · cintura70/cadera100 -> M', in: { heightCm: 165, weightKg: 65, fit: 2, waistCm: 70, hipCm: 100 }, chart: PANTS, expect: 'M' },
      { name: 'Vestido · muy delgada · muy pegada -> S + tooLoose', in: { heightCm: 170, weightKg: 48, fit: 0 }, chart: DRESS, expect: 'S', tooLoose: true }
    ];

    var pass = 0, fail = 0;
    cases.forEach(function (c) {
      var ok = true, detail = '';
      if (c.kind === 'est') {
        var e = estimateBodyCirc(c.h, c.w, 'female');
        ['bust', 'waist', 'hip'].forEach(function (k) {
          if (Math.abs(e[k] - c.expect[k]) > c.tol) { ok = false; }
        });
        detail = 'bust=' + round1(e.bust) + ' waist=' + round1(e.waist) + ' hip=' + round1(e.hip);
      } else if (c.kind === 'bra') {
        var bb = bustFromBra(c.ub, c.cup);
        ok = Math.abs(bb - c.expect) <= c.tol;
        detail = 'bust=' + bb;
      } else {
        var r = recommendSize(c.in, c.chart);
        ok = r.size === c.expect;
        if (c.tooTight != null) ok = ok && r.tooTight === c.tooTight;
        if (c.tooLoose != null) ok = ok && r.tooLoose === c.tooLoose;
        detail = 'size=' + r.size + ' alt=' + r.alt + ' conf=' + r.confidence +
          ' bind=' + r.bindingMeasure + ' tight=' + r.tooTight + ' loose=' + r.tooLoose +
          ' body=' + JSON.stringify(r.bodyCirc);
      }
      if (ok) { pass++; } else { fail++; }
      log((ok ? '  OK  ' : ' FAIL ') + c.name + '  ::  ' + detail);
    });
    log('\n' + pass + ' pasan, ' + fail + ' fallan.');
    return { pass: pass, fail: fail };
  }

  function log(s) { if (typeof console !== 'undefined') console.log(s); }

  // ==========================================================================================
  //  Export
  // ==========================================================================================
  var api = {
    recommendSize: recommendSize,
    estimateBodyCirc: estimateBodyCirc,
    bustFromBra: bustFromBra,
    inToCm: inToCm,
    lbToKg: lbToKg,
    CONFIG: CONFIG,
    selfTest: selfTest
  };

  root.GrSizeCalc = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof process !== 'undefined' && process.argv && process.argv.indexOf('--selftest') !== -1) selfTest();

})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
